/**
 * Fait : annonce un message aux technologies d'assistance sans voler le focus —
 *   « poli » pour ce qui peut attendre la fin de la phrase en cours, « urgent »
 *   pour ce qui ne le peut pas.
 *
 * Ne fait pas : il n'affiche aucune décoration et ne décide pas de l'urgence à
 *   la place de l'appelant. Tout annoncer en urgent revient à ne rien annoncer :
 *   la lecture est coupée si souvent qu'elle devient inutilisable.
 */

import type { ComponentPropsWithRef, ReactNode } from 'react';

/*
 * AVEC LA RÉFÉRENCE, et pas seulement les attributs. Un appelant doit pouvoir
 * porter le regard SUR l'annonce — la faire venir dans la vue et la donner à
 * lire —, ce qui exige de tenir son nœud. Sans cela, un message affiché tout
 * en haut d'un écran dont les formulaires sont tout en bas est écrit sans être
 * vu : c'est ce qui a fait croire à Charles, le 2026-08-27, qu'inscrire un
 * modèle n'avait rien donné.
 */
export interface AnnonceProps extends ComponentPropsWithRef<'p'> {
  readonly urgence?: 'poli' | 'urgent';
  readonly visible?: boolean;

  /**
   * Ce dont il s'agit — un REFUS reçoit la marque visuelle des refus, une
   * information n'en reçoit aucune.
   *
   * L'urgence et la nature sont deux choses : « l'accès de Mimi est fermé »
   * est urgent à annoncer et n'est pas un échec. Sans cette distinction, tout
   * ce qui pressait devenait rouge, ou — ce qui s'est passé ici — rien ne
   * l'était : les refus s'affichaient en texte noir ordinaire, indiscernables
   * de l'aide grise pour qui voit mal.
   */
  readonly nature?: 'information' | 'refus';

  readonly children: ReactNode;
}

export function Annonce({
  urgence = 'poli',
  visible = true,
  nature = 'information',
  children,
  ...reste
}: AnnonceProps) {
  const classes = [
    visible ? undefined : 'pour-lecteur-decran',
    nature === 'refus' ? 'message-erreur' : undefined,
  ].filter((classe) => classe !== undefined);

  return (
    <p
      role={urgence === 'urgent' ? 'alert' : 'status'}
      aria-live={urgence === 'urgent' ? 'assertive' : 'polite'}
      className={classes.length === 0 ? undefined : classes.join(' ')}
      /*
       * LE RESTE EST RELAYÉ. Sans cela, une marque posée sur une annonce —
       * `data-role`, un identifiant — n'atteignait JAMAIS le DOM : elle était
       * avalée en silence, et tout témoin qui la sélectionnait mesurait du
       * vide en croyant mesurer l'écran. Trouvé par une épreuve indépendante
       * le 2026-08-25.
       */
      {...reste}
    >
      {children}
    </p>
  );
}
